/**
 * @module LoadingBridge
 * @package @geenius/adapters/react-native
 * @description Native loading indicator bridge for async adapter states.
 */

import type { JSX } from "react";
import type { StyleProp, ViewStyle } from "react-native";
import { ActivityIndicator, StyleSheet, Text, View } from "../lib/native";
import { semanticColors } from "./TokenBridge";

/**
 * Props for the native loading bridge.
 *
 * @property label Accessible label and visible loading text.
 * @property size Native activity indicator size.
 * @property style Optional native style merged onto the bridge container.
 * @property testID Optional deterministic selector for tests and Storybook.
 */
export interface LoadingBridgeProps {
  label?: string;
  size?: "small" | "large";
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

/**
 * Renders a native loading indicator for adapter-driven async states.
 *
 * @param props Loading bridge props including label and indicator size.
 * @returns A React Native progressbar-style loading row.
 * @example
 * ```tsx
 * <LoadingBridge label="Checking adapters" size="large" />
 * ```
 */
export function LoadingBridge({
  label = "Loading adapters",
  size = "small",
  style,
  testID,
}: LoadingBridgeProps): JSX.Element {
  return (
    <View
      accessibilityLabel={label}
      accessibilityRole="progressbar"
      style={[styles.container, style]}
      testID={testID ?? "adapter-loading-bridge"}
    >
      <ActivityIndicator color={semanticColors.brand.bg} size={size} />
      <Text style={styles.label}>{label}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
    paddingVertical: 8,
  },
  label: {
    color: semanticColors.surface.textSoft,
    fontSize: 13,
    fontWeight: "600",
  },
});
